<template>
  <aside
    :class="isCollapsed ? 'sm-collapsed' : 'sm-expanded'"
    class="sm-aside"
  >
    <div class="sm-card">
      <!-- ==================== Header: logo + collapse ==================== -->
      <div class="sm-head">
        <div v-show="!isCollapsed" class="sm-logo-wrap">
          <span class="sm-logo-text" title="Secure Payment System">SPS</span>
        </div>
        <div v-show="isCollapsed" class="sm-logo-mark" title="Secure Payment System">
          <span class="sm-logo-mark-text">SPS</span>
        </div>
        <button
          @click="toggleCollapse"
          class="sm-toggle"
          :title="isCollapsed ? 'Expand menu' : 'Collapse menu'"
        >
          <svg v-if="isCollapsed" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M13 5l7 7-7 7M5 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 19l-7-7 7-7m8 14l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>

      <!-- ==================== Nav sections ==================== -->
      <nav class="sm-nav">
        <div v-for="(section, si) in navSections" :key="'sec-' + si" class="sm-section">
          <!--
            Sections with a title (Operations / Management / Insights / Tools /
            Account) get a click-to-collapse header. The chevron rotates and
            the list `v-show`s off. Only rendered when the sidebar itself is
            expanded — in the icons-only sidebar there is no title bar, so
            per-section collapse doesn't apply (and would just orphan icons).
          -->
          <button
            v-if="section.title && !isCollapsed"
            type="button"
            class="sm-section-title sm-section-toggle"
            :class="{ 'is-open': !isSectionCollapsed(section.title) }"
            @click="toggleSection(section.title)"
            :aria-expanded="!isSectionCollapsed(section.title)"
          >
            <span>{{ section.title }}</span>
            <svg class="sm-section-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
              <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <ul
            v-show="isCollapsed || !section.title || !isSectionCollapsed(section.title)"
            class="sm-list"
          >
            <li v-for="item in section.items" :key="item.to">
              <router-link
                :to="item.to"
                class="sm-item"
                :class="{ 'sm-item-collapsed': isCollapsed }"
                :title="isCollapsed ? item.label : ''"
                active-class="is-active"
              >
                <span class="sm-icon" v-html="iconSvg(item.icon)"></span>
                <span v-show="!isCollapsed" class="sm-label">{{ item.label }}</span>
              </router-link>
            </li>
          </ul>
        </div>
      </nav>

      <!-- ==================== Footer: pay-in limit + user + logout ==================== -->
      <div class="sm-footer" :class="{ 'sm-footer-collapsed': isCollapsed }">
        <div
          v-if="!isCollapsed && showPayInLimit"
          class="sm-limit"
          title="Today's remaining pay-in limit"
        >
          <span class="limit-label">Pay-in limit</span>
          <span class="limit-value">₹{{ formatMoney(headerData.payInLimit) }}</span>
        </div>

        <div class="sm-user" :class="{ 'sm-user-collapsed': isCollapsed }">
          <div class="sm-avatar" :title="username">{{ userInitial }}</div>
          <div v-show="!isCollapsed" class="sm-user-meta">
            <div class="sm-user-name">{{ username || '—' }}</div>
            <div v-if="showPayInLimit" class="sm-user-role">
              {{ headerData.role === 'bank' ? 'Bank' : 'Branch' }}
            </div>
          </div>
          <button
            v-if="isLoggedIn"
            @click="logout"
            class="sm-logout"
            :class="{ 'sm-logout-collapsed': isCollapsed }"
            title="Log out"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
import http from "../../http-common";
import { getScopedSocket } from "../../utils/socket";

const ICONS = {
  dashboard: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></svg>`,
  users: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="9" cy="8" r="3.5"/><path d="M2.5 20a6.5 6.5 0 0113 0" stroke-linecap="round"/><path d="M16 11a3 3 0 100-6M22 20a5 5 0 00-4-4.9" stroke-linecap="round"/></svg>`,
  bank: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 10l9-6 9 6M4 10v9M20 10v9M8 10v9M12 10v9M16 10v9M2 21h20" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  payin: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 3v14M6 11l6 6 6-6M4 21h16" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  payout: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 21V7M6 13l6-6 6 6M4 3h16" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  document: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6z" stroke-linecap="round" stroke-linejoin="round"/><path d="M14 3v6h6M8 13h8M8 17h5" stroke-linecap="round"/></svg>`,
  chart: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 3v18h18" stroke-linecap="round"/><path d="M7 15l4-4 4 4 5-5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  reconcile: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M21 12a9 9 0 01-15 6.7L3 21m0-9a9 9 0 0115-6.7L21 3" stroke-linecap="round" stroke-linejoin="round"/><path d="M21 3v6h-6M3 21v-6h6" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  extension: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M14 4h6v6M20 4l-7 7M10 20H4v-6M4 20l7-7" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  key: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="8" cy="15" r="4"/><path d="M10.85 12.15L19 4M18 5l3 3M15 8l3 3" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  lock: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0116 0" stroke-linecap="round"/></svg>`,
  route: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="6" cy="19" r="2"/><circle cx="18" cy="5" r="2"/><path d="M8 19h5a4 4 0 004-4v-4a4 4 0 00-4-4H8" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
};

const COLLAPSE_KEY = "sidemenu_collapsed";
const SECTION_COLLAPSE_KEY = "sidemenu_section_collapsed";

// Rehydrate per-section collapse state as a plain object keyed by section
// title. Bad / missing JSON silently resets — nothing here is critical, just
// UI preference.
function loadSectionCollapse() {
  try {
    const raw = localStorage.getItem(SECTION_COLLAPSE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_) {
    return {};
  }
}

export default {
  name: "side-menu",
  emits: ["collapse-change"],
  data() {
    return {
      isCollapsed: localStorage.getItem(COLLAPSE_KEY) === "true",
      collapsedSections: loadSectionCollapse(),
      headerData: null,
      socket: null,
    };
  },
  async mounted() {
    if (this.isLoggedIn) {
      await this.fetchHeaderData();
      this.connectSocket();
    }
  },
  beforeUnmount() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  },
  watch: {
    isLoggedIn(newVal) {
      if (newVal) {
        this.fetchHeaderData();
        this.connectSocket();
      } else {
        this.headerData = null;
        if (this.socket) {
          this.socket.disconnect();
          this.socket = null;
        }
      }
    },
  },
  computed: {
    isOrderCreator() { return !!this.$store.getters.isOrderCreator; },
    isAdmin()        { return !!this.$store.getters.isAdmin; },
    isSubAdmin()     { return !!this.$store.getters.isSubAdmin; },
    isSuperAdmin()   { return !!this.$store.getters.isSuperAdmin; },
    isClient()       { return !!this.$store.getters.isClient; },
    isClientPartner() { return !!this.$store.getters.isClientPartner; },
    // Global toggle (02112026): show the Dashboard to vendors. From the JWT
    // claim persisted to localStorage at login.
    vendorDashboardEnabled() { return localStorage.getItem("vendorDashboardEnabled") === "1"; },
    isSupporting()   { return !!this.$store.getters.isSupporting; },
    canManageClients() { return !!this.$store.getters.canManageClients; },
    isLoggedIn() { return this.$store.getters.isAuthenticated; },
    username()   { return this.$store.getters.username; },
    userInitial() {
      const n = String(this.username || "").trim();
      return n ? n.charAt(0).toUpperCase() : "?";
    },
    showPayInLimit() {
      return !!(this.headerData && (this.headerData.role === "bank" || this.headerData.role === "branch"));
    },
    isShowPayInStatistics() {
      if (this.$store.getters.isUser) return this.$store.getters.payInStatus != "0";
      if (this.$store.getters.isOrderCreator || this.$store.getters.isSupport) return false;
      if (this.$store.getters.isClient) return true;
      return true;
    },
    isShowPayOutStatistics() {
      // Banks (role='user') are no longer involved in the payout flow.
      // Payouts are now handled exclusively by branches (subadmin).
      if (this.$store.getters.isUser) return false;
      if (this.$store.getters.isOrderCreator || this.$store.getters.isSupport) return false;
      if (this.$store.getters.isClient) return true;
      return true;
    },

    navSections() {
      // Supporting users (21092026): menu is driven by EFFECTIVE PERMISSIONS,
      // not role — a supporting user inherits the parent role but must only
      // see what they can actually use (Phase-1 clean slice). Keeps them off
      // pages that would deny-by-default 403 on the API. Main users fall
      // through to the unchanged role-based logic below.
      if (this.isSupporting) return this.supportingSections();

      // Superadmin: separate curated set.
      if (this.isSuperAdmin) {
        return [
          { title: null, items: [
            { to: "/dashboard", label: "Dashboard", icon: "dashboard" },
          ]},
          { title: "Management", items: [
            { to: "/vendor",   label: "Vendors",  icon: "users" },
            { to: "/clients",  label: "Clients",  icon: "users" },
            { to: "/client-partners", label: "Client Partners", icon: "users" },
            { to: "/routing",  label: "Routing",  icon: "route" },
          ]},
          { title: "Insights", items: [
            { to: "/admin-report",   label: "Admin Report", icon: "chart" },
            { to: "/payin/reconcile", label: "Telly",       icon: "reconcile" },
          ]},
          { title: "Account", items: [
            { to: "/account", label: "Account", icon: "lock" },
          ]},
        ];
      }

      // Non-superadmin: build per-role.
      const sections = [];
      // Dashboard is hidden for vendor (admin) UNLESS the global toggle
      // VENDOR_DASHBOARD_ENABLED is on (JWT claim → localStorage). Every other
      // non-superadmin role always gets it. (Router mirrors this gate.)
      if (!this.isAdmin || this.vendorDashboardEnabled) {
        sections.push({ title: null, items: [
          { to: "/dashboard", label: "Dashboard", icon: "dashboard" },
        ]});
      }

      const ops = [];
      if (this.isShowPayInStatistics)  ops.push({ to: "/payin",        label: "Pay In",       icon: "payin"  });
      if (this.isShowPayOutStatistics) ops.push({ to: "/payout",       label: "Pay Out",      icon: "payout" });
      if (this.isOrderCreator)         ops.push({ to: "/manual-order", label: "Manual Order", icon: "payout" });
      if (ops.length) sections.push({ title: "Operations", items: ops });

      const mgmt = [];
      if (this.isAdmin)                          mgmt.push({ to: "/branches", label: "Branches", icon: "bank"  });
      if (this.isAdmin || this.isSubAdmin)       mgmt.push({ to: "/banks",    label: "Banks",    icon: "bank"  });
      if (this.isAdmin && this.canManageClients) mgmt.push({ to: "/clients",  label: "Clients",  icon: "users" });
      if (mgmt.length) sections.push({ title: "Management", items: mgmt });

      const insights = [];
      if (this.isAdmin)                          insights.push({ to: "/callback-logs",   label: "Callback Logs",  icon: "document" });
      if (this.isAdmin || this.isSubAdmin)       insights.push({ to: "/upload-history",  label: "Upload History", icon: "document" });
      if (this.isAdmin || this.isSubAdmin)       insights.push({ to: "/payin/reconcile", label: "Telly",          icon: "reconcile" });
      if (this.isAdmin && this.canManageClients) insights.push({ to: "/admin-report",    label: "Admin Report",   icon: "chart" });
      // Client Partner: their own clients report (view-only, client-scoped).
      if (this.isClientPartner)                  insights.push({ to: "/client-partner-report", label: "My Clients Report", icon: "chart" });
      if (insights.length) sections.push({ title: "Insights", items: insights });

      const tools = [];
      if (this.isSubAdmin) tools.push({ to: "/extensions",     label: "Extensions",     icon: "extension" });
      if (this.isSubAdmin) tools.push({ to: "/reset-password", label: "Reset Password", icon: "key" });
      if (tools.length) sections.push({ title: "Tools", items: tools });

      sections.push({ title: "Account", items: [
        { to: "/account", label: "Account", icon: "lock" },
      ]});

      return sections;
    },
  },
  methods: {
    // Permission-driven menu for supporting users. Only the Phase-1 enforced
    // surfaces are offered; add entries here as more endpoints are mapped in
    // the backend routeMap. Account is always shown (self-service).
    supportingSections() {
      const perms = this.$store.getters.effectivePermissions || [];
      const has = (k) => perms.includes(k);
      const sections = [];

      const primary = [];
      if (has("dashboard.view")) primary.push({ to: "/dashboard", label: "Dashboard", icon: "dashboard" });
      if (primary.length) sections.push({ title: null, items: primary });

      const ops = [];
      if (has("payin.list.view")) ops.push({ to: "/payin", label: "Pay In", icon: "payin" });
      if (has("payout.list.view")) ops.push({ to: "/payout", label: "Pay Out", icon: "payout" });
      if (ops.length) sections.push({ title: "Operations", items: ops });

      const mgmt = [];
      if (has("branch.list.view")) mgmt.push({ to: "/branches", label: "Branches", icon: "bank" });
      if (has("bank.list.view")) mgmt.push({ to: "/banks", label: "Banks", icon: "bank" });
      if (has("client.list.view")) mgmt.push({ to: "/clients", label: "Clients", icon: "users" });
      if (mgmt.length) sections.push({ title: "Management", items: mgmt });

      const insights = [];
      if (has("callback.logs.view")) insights.push({ to: "/callback-logs", label: "Callback Logs", icon: "document" });
      if (has("payin.statement.view") || has("upload.history.view")) insights.push({ to: "/upload-history", label: "Upload History", icon: "document" });
      if (has("settlement.view")) insights.push({ to: "/payin/reconcile", label: "Telly", icon: "reconcile" });
      if (has("report.view")) insights.push({ to: "/reports", label: "Reports", icon: "chart" });
      if (has("report.admin.view")) insights.push({ to: "/admin-report", label: "Admin Report", icon: "chart" });
      if (insights.length) sections.push({ title: "Insights", items: insights });

      sections.push({ title: "Account", items: [{ to: "/account", label: "Account", icon: "lock" }] });
      return sections;
    },
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      localStorage.setItem(COLLAPSE_KEY, this.isCollapsed);
      this.$emit("collapse-change", this.isCollapsed);
    },
    isSectionCollapsed(title) {
      return !!this.collapsedSections[title];
    },
    toggleSection(title) {
      if (!title) return;
      this.collapsedSections = {
        ...this.collapsedSections,
        [title]: !this.collapsedSections[title],
      };
      try {
        localStorage.setItem(
          SECTION_COLLAPSE_KEY,
          JSON.stringify(this.collapsedSections)
        );
      } catch (_) {
        // Storage full / disabled — collapse still works for this session.
      }
    },
    iconSvg(name) {
      return ICONS[name] || ICONS.document;
    },
    formatMoney(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    async fetchHeaderData() {
      try {
        const { data } = await http.get("/users/header-data");
        this.headerData = data;
      } catch (err) {
        console.error("Failed to load header data", err);
      }
    },
    connectSocket() {
      const username = this.username;
      if (!username) return;
      try {
        // Re-entry hardening (24082026): a username change re-invokes this
        // without unmount; clean the previous scope's listeners first or
        // they leak on the shared socket (pre-existing pattern — it used
        // to orphan a whole connection).
        if (this.socket) this.socket.disconnect();
        this.socket = getScopedSocket();
        this.socket.on(`payin-limit-updated-${username}`, (data) => {
          if (this.headerData && data && typeof data.payInLimit !== "undefined") {
            this.headerData.payInLimit = data.payInLimit;
          }
        });
      } catch (e) {
        console.error("Sidebar socket connect failed", e);
      }
    },
    async logout() {
      try {
        if (this.socket) {
          this.socket.disconnect();
          this.socket = null;
        }
        await this.$store.dispatch("logout");
        this.$router.replace("/");
      } catch (err) {
        console.error("Logout failed", err);
      }
    },
  },
};
</script>

<style scoped>
/* ==================== Outer aside ==================== */
.sm-aside {
  height: 100%;
  position: fixed;
  z-index: 10;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  padding: 12px;
  box-sizing: border-box;
}
/* Widths kept fixed at every breakpoint so the dashboard layout's
   placeholder (also 260px / 72px) aligns 1:1 — no gap, no overlap. */
.sm-expanded  { width: 260px; }
.sm-collapsed { width: 72px; }

.sm-card {
  height: 100%;
  background: #fbfaf7;
  border: 1px solid #ede9df;
  border-radius: 16px;
  box-shadow: 0 8px 24px -14px rgba(27, 42, 71, 0.10);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ==================== Header ==================== */
.sm-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 12px 10px;
  flex-shrink: 0;
  border-bottom: 1px solid #f1ede2;
  gap: 8px;
  min-height: 60px;
}
/* Collapsed sidebar is 72px — logo-mark + toggle side-by-side don't
   fit. Stack them vertically and center so both stay clickable. */
.sm-aside.sm-collapsed .sm-head {
  flex-direction: column;
  padding: 10px 6px;
  gap: 6px;
  min-height: auto;
}
.sm-aside.sm-collapsed .sm-toggle {
  background: #f4efe4;
  color: #1b2a47;
  border: 1px solid #ede9df;
}
.sm-aside.sm-collapsed .sm-toggle:hover {
  background: #eae2cf;
}
.sm-logo-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  min-width: 0;
}
.sm-logo {
  max-height: 84px;
  width: auto;
  object-fit: contain;
}
.sm-logo-text {
  font-weight: 800;
  font-size: 32px;
  letter-spacing: 2px;
  color: #d94654;
}
.sm-logo-mark {
  width: 36px; height: 36px;
  border-radius: 9px;
  overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 3px 8px -3px rgba(217, 70, 84, 0.45);
  flex-shrink: 0;
  background: transparent;
}
.sm-logo-mark-text {
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.5px;
  color: #fff;
}
.sm-logo-mark {
  background: #d94654;
}
.sm-logo-mark img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.sm-toggle {
  flex-shrink: 0;
  width: 30px; height: 30px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #9d968a;
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.sm-toggle:hover { background: #f4efe4; color: #1b2a47; }
.sm-toggle svg { width: 14px; height: 14px; }

/* ==================== Nav ==================== */
.sm-nav {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 10px 20px;
}
.sm-nav::-webkit-scrollbar { width: 5px; }
.sm-nav::-webkit-scrollbar-thumb { background: #e4e0d3; border-radius: 999px; }
.sm-nav::-webkit-scrollbar-thumb:hover { background: #cdc7b8; }

.sm-section + .sm-section {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #f1ede2;
}

.sm-section-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #a8a08c;
  padding: 0 12px 8px;
}

/*
 * Clickable variant — used when the section is collapsible. Kept visually
 * identical to the static .sm-section-title so the eyebrow still reads as an
 * eyebrow; the only affordance is the chevron on the right that rotates when
 * open.
 */
.sm-section-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: 0;
  cursor: pointer;
  padding: 6px 12px 8px;
  border-radius: 8px;
  font-family: inherit;
  text-align: left;
  color: #a8a08c;
  transition: background 0.15s, color 0.15s;
}
.sm-section-toggle:hover {
  background: #f5f0e4;
  color: #6f6a58;
}
.sm-section-toggle:focus-visible {
  outline: 2px solid #1B2A47;
  outline-offset: 2px;
}
.sm-section-chevron {
  width: 12px;
  height: 12px;
  transition: transform 0.2s ease;
  transform: rotate(-90deg);
  flex-shrink: 0;
}
.sm-section-toggle.is-open .sm-section-chevron {
  transform: rotate(0deg);
}

.sm-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* ==================== Nav item ==================== */
.sm-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  border-radius: 10px;
  text-decoration: none;
  color: #4b5566;
  transition: background 0.15s, color 0.15s;
  position: relative;
}
.sm-item:hover {
  background: #f5f0e4;
  color: #1b2a47;
}
.sm-item.is-active {
  background: #eef2f7;
  color: #1B2A47;
  font-weight: 600;
}
.sm-item.is-active::before {
  content: "";
  position: absolute;
  left: 0;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: linear-gradient(180deg, #2C3E63, #1B2A47);
}

.sm-icon {
  width: 20px; height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: currentColor;
  opacity: 0.85;
  flex-shrink: 0;
  transition: opacity 0.15s;
}
.sm-icon :deep(svg) { width: 18px; height: 18px; }
.sm-item:hover .sm-icon,
.sm-item.is-active .sm-icon { opacity: 1; }

.sm-label {
  font-size: 13px;
  line-height: 1.2;
  letter-spacing: -0.005em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ==================== Collapsed variants ==================== */
.sm-item-collapsed {
  justify-content: center;
  padding: 10px 8px;
}
.sm-item-collapsed.is-active::before {
  top: 6px;
  bottom: 6px;
}

/* ==================== Footer ==================== */
.sm-footer {
  flex-shrink: 0;
  padding: 12px 12px 14px;
  border-top: 1px solid #f1ede2;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fbfaf7;
}
.sm-footer-collapsed {
  padding: 10px 8px 12px;
}

.sm-limit {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 10px;
  background: #f6f2e6;
  border: 1px solid #ece6d3;
}
.limit-label {
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 700;
}
.limit-value {
  font-size: 13px;
  font-weight: 700;
  color: #1B2A47;
  font-variant-numeric: tabular-nums;
}

.sm-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 12px;
  transition: background 0.15s;
}
.sm-user:hover { background: #f5f0e4; }

.sm-user-collapsed {
  flex-direction: column;
  gap: 6px;
  padding: 4px;
}

.sm-avatar {
  width: 34px; height: 34px;
  border-radius: 10px;
  background: linear-gradient(135deg, #1b2a47, #2c3e63);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 3px 8px -3px rgba(27, 42, 71, 0.35);
}

.sm-user-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.sm-user-name {
  font-size: 12.5px;
  font-weight: 600;
  color: #1b2a47;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}
.sm-user-role {
  font-size: 10px;
  color: #94a3a5;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 600;
  line-height: 1.2;
}

.sm-logout {
  flex-shrink: 0;
  width: 30px; height: 30px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #a89f8b;
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.sm-logout:hover {
  background: #eef2f7;
  color: #1B2A47;
}
.sm-logout svg { width: 16px; height: 16px; }
.sm-logout-collapsed { width: 32px; height: 32px; }
</style>
